/**
 * Print crop geometry (ALBAU-922).
 *
 * The photo editor hands over its framing (zoom, move, rotate, flip and where the mask sits)
 * and the customer's original upload. This module holds the maths that lets a renderer redraw
 * the original with exactly that framing, so the print file shows the same picture as the
 * editor but at the original's resolution rather than the 1250 px editor canvas.
 *
 * It mirrors the editor's draw (canvasRenderer.ts), so every original pixel lands in the same
 * place in the print as it does in the editor. It is pure: no canvas, no DOM.
 */

/** Q1: the print resolution we aim for. */
export const PRINT_TARGET_DPI = 300;

/** Q3: the largest window (in inches) a customer's picture is printed in. */
export const PRINT_WINDOW_MAX_INCHES = 4;

/** The longest edge of a print file in pixels: 300 DPI over a 4 inch window, so 1200. */
export const PRINT_CROP_MAX_LONG_EDGE_PX = PRINT_TARGET_DPI * PRINT_WINDOW_MAX_INCHES;

/** Today's padding around the mask, as in `cropMaskedImageWithPadding`. */
export const PRINT_CROP_PADDING_PX = 10;

/** The keepsake card's JPEG quality, as in `cropImageToView.ts`. */
export const PRINT_CROP_JPEG_QUALITY = 0.92;

/** The `ctx.setTransform(a, b, c, d, e, f)` order: x' = a*x + c*y + e, y' = b*x + d*y + f. */
export type Matrix2D = [number, number, number, number, number, number];

/** What the editor knew when it drew the photo. All numbers are editor canvas pixels unless named otherwise. */
export interface PrintCropTransform {
  canvasWidth: number;
  canvasHeight: number;
  /** The original's natural size. */
  imageWidth: number;
  imageHeight: number;
  /** The contain-fit size of the photo on the editor canvas. */
  drawWidth: number;
  drawHeight: number;
  /** Degrees. */
  rotation: number;
  scale: number;
  flipX: boolean;
  offsetX: number;
  offsetY: number;
  maskCenterX: number;
  maskCenterY: number;
  maskWidth: number;
}

/** What the editor hands over: the original upload, the mask and the framing. */
export interface PrintSource {
  file: File;
  maskBlob: Blob;
  transform: PrintCropTransform;
}

export interface PrintCropGeometry {
  /** Today's crop rectangle on the editor canvas. */
  cropX: number;
  cropY: number;
  cropWidth: number;
  cropHeight: number;
  /** The print file's size in whole pixels. */
  outputWidth: number;
  outputHeight: number;
  /** Print pixels per editor canvas pixel. */
  scaleX: number;
  scaleY: number;
}

const NUMERIC_FIELDS: Array<keyof PrintCropTransform> = [
  'canvasWidth',
  'canvasHeight',
  'imageWidth',
  'imageHeight',
  'drawWidth',
  'drawHeight',
  'rotation',
  'scale',
  'offsetX',
  'offsetY',
  'maskCenterX',
  'maskCenterY',
  'maskWidth'
];

const POSITIVE_FIELDS: Array<keyof PrintCropTransform> = [
  'canvasWidth',
  'canvasHeight',
  'imageWidth',
  'imageHeight',
  'drawWidth',
  'drawHeight',
  'scale',
  'maskWidth'
];

/**
 * Whether a value has the shape the print crop needs: a file, a mask blob and a usable transform.
 *
 * @param {unknown} value - Whatever the photo editor handed over.
 * @returns {boolean} True when it is a usable print source.
 */
export function isPrintSource(value: unknown): value is PrintSource {
  if (!value || typeof value !== 'object') return false;
  const { file, maskBlob, transform } = value as Record<string, unknown>;
  if (!(file instanceof File) || !(maskBlob instanceof Blob)) return false;
  if (!transform || typeof transform !== 'object') return false;
  const fields = transform as Record<string, unknown>;
  const isFiniteNumber = (key: string) => typeof fields[key] === 'number' && Number.isFinite(fields[key]);
  if (!NUMERIC_FIELDS.every(isFiniteNumber)) return false;
  if (!POSITIVE_FIELDS.every((key) => (fields[key] as number) > 0)) return false;
  return typeof fields.flipX === 'boolean';
}

const translate = (x: number, y: number): Matrix2D => [1, 0, 0, 1, x, y];
const scale = (x: number, y: number): Matrix2D => [x, 0, 0, y, 0, 0];
const rotate = (r: number): Matrix2D => [Math.cos(r), Math.sin(r), -Math.sin(r), Math.cos(r), 0, 0];

/** Multiplies two matrices the way the canvas does: `n` is applied first, then `m`. */
const multiply = (m: Matrix2D, n: Matrix2D): Matrix2D => [
  m[0] * n[0] + m[2] * n[1],
  m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3],
  m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4],
  m[1] * n[4] + m[3] * n[5] + m[5]
];

/**
 * Applies a 2D matrix to a point.
 *
 * @param {Matrix2D} matrix - The matrix, in canvas `setTransform` order.
 * @param {number} x - The point's x.
 * @param {number} y - The point's y.
 * @returns {{ x: number, y: number }} The transformed point.
 */
export function applyMatrix(matrix: Matrix2D, x: number, y: number): { x: number; y: number } {
  return {
    x: matrix[0] * x + matrix[2] * y + matrix[4],
    y: matrix[1] * x + matrix[3] * y + matrix[5]
  };
}

/**
 * The transform the photo editor draws the photo with, in the same order as
 * `canvasRenderer.ts:305-309`: move to the mask centre, rotate, scale (mirrored for flip), then
 * move by the offset.
 */
export function editorMatrix(transform: PrintCropTransform): Matrix2D {
  return [
    translate(transform.maskCenterX, transform.maskCenterY),
    rotate((transform.rotation * Math.PI) / 180),
    scale(transform.scale * (transform.flipX ? -1 : 1), transform.scale),
    translate(transform.offsetX, transform.offsetY)
  ].reduce(multiply);
}

/**
 * Works out today's crop rectangle and the size of the print file.
 * `maskAspect` is the mask's height / width.
 */
export function computePrintCropGeometry({
  transform,
  maskAspect,
  maxLongEdge = PRINT_CROP_MAX_LONG_EDGE_PX,
  padding = PRINT_CROP_PADDING_PX
}: {
  transform: PrintCropTransform;
  maskAspect: number;
  maxLongEdge?: number;
  padding?: number;
}): PrintCropGeometry {
  if (!Number.isFinite(maskAspect) || maskAspect <= 0) throw new Error('Invalid mask aspect');
  const maskHeight = transform.maskWidth * maskAspect;
  // Today's crop rectangle (cropMaskedImageWithPadding): the mask box plus padding, clamped to the canvas.
  const left = Math.max(0, transform.maskCenterX - transform.maskWidth / 2 - padding);
  const top = Math.max(0, transform.maskCenterY - maskHeight / 2 - padding);
  const right = Math.min(transform.canvasWidth, transform.maskCenterX + transform.maskWidth / 2 + padding);
  const bottom = Math.min(transform.canvasHeight, transform.maskCenterY + maskHeight / 2 + padding);
  const cropWidth = right - left;
  const cropHeight = bottom - top;
  if (!(cropWidth > 0 && cropHeight > 0)) throw new Error('The mask is outside the editor canvas');
  // Original pixels per editor pixel where the photo is drawn: every pixel the original has.
  const nativeScale = transform.imageWidth / (transform.drawWidth * transform.scale);
  const capped = Math.min(nativeScale, maxLongEdge / Math.max(cropWidth, cropHeight));
  // Never smaller than today's crop (one print pixel per editor pixel).
  const multiplier = Math.max(1, capped);
  const outputWidth = Math.max(1, Math.round(cropWidth * multiplier));
  const outputHeight = Math.max(1, Math.round(cropHeight * multiplier));
  return {
    cropX: left,
    cropY: top,
    cropWidth,
    cropHeight,
    outputWidth,
    outputHeight,
    scaleX: outputWidth / cropWidth,
    scaleY: outputHeight / cropHeight
  };
}

/** The transform that draws the photo into the print file: the editor's draw, shifted to the crop and scaled up. */
export function printCropMatrix(transform: PrintCropTransform, geometry: PrintCropGeometry): Matrix2D {
  return [
    scale(geometry.scaleX, geometry.scaleY),
    translate(-geometry.cropX, -geometry.cropY),
    editorMatrix(transform)
  ].reduce(multiply);
}

/** Where the mask's bounding box sits on the print file. */
export function maskRectOnOutput(
  transform: PrintCropTransform,
  geometry: PrintCropGeometry,
  maskAspect: number
): { x: number; y: number; width: number; height: number } {
  const maskHeight = transform.maskWidth * maskAspect;
  return {
    x: (transform.maskCenterX - transform.maskWidth / 2 - geometry.cropX) * geometry.scaleX,
    y: (transform.maskCenterY - maskHeight / 2 - geometry.cropY) * geometry.scaleY,
    width: transform.maskWidth * geometry.scaleX,
    height: maskHeight * geometry.scaleY
  };
}
